import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Event as EventIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useBookMyOpAuth();
  const { 
    notifications, 
    loading, 
    error,
    markAsRead, 
    markAllAsRead,
    fetchNotifications,
    clearError 
  } = useNotifications();
  
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Only fetch notifications once when component mounts
    fetchNotifications();
  }, [isAuthenticated, navigate]); // Removed fetchNotifications from dependencies

  const getNotificationIcon = useCallback((type) => {
    switch (type) {
      case 'appointment':
      case 'appointment_confirmed':
      case 'appointment_cancelled':
      case 'appointment_reminder':
        return <EventIcon color="primary" />;
      case 'communication':
      case 'new_ticket':
      case 'ticket_reply':
        return <MessageIcon color="secondary" />;
      case 'reminder':
        return <ScheduleIcon color="warning" />;
      default:
        return <NotificationsIcon />;
    }
  }, []);

  const getNotificationColor = useCallback((type) => {
    switch (type) {
      case 'appointment_confirmed':
        return 'success';
      case 'appointment_cancelled':
        return 'error';
      case 'appointment_reminder':
        return 'warning';
      case 'new_ticket':
        return 'info';
      default:
        return 'default';
    }
  }, []);

  const formatNotificationTime = useCallback((createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((now - created) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  }, []);

  const filteredNotifications = useCallback(() => {
    return notifications.filter(notification => {
      if (filter === 'all') return true;
      if (filter === 'unread') return !notification.is_read;
      return notification.notification_type === filter;
    });
  }, [notifications, filter]);

  const handleNotificationClick = useCallback(async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Handle navigation
    if (notification.appointment) {
      navigate(`/appointments/${notification.appointment}`);
    } else if (notification.communication) {
      navigate(`/communications/${notification.communication}`);
    }
  }, [markAsRead, navigate]);

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    setPage(1); // Reset to first page when filter changes
  }, []);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalPages = Math.ceil(filteredNotifications().length / pageSize);
  const paginatedNotifications = filteredNotifications().slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, pb: { xs: '120px', md: '32px' } }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight={600}>
          Notifications
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Actions Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                Filters
              </Button>
              {filter !== 'all' && (
                <Chip 
                  label={`Filtered: ${filter}`} 
                  color="primary" 
                  size="small"
                  onDelete={() => handleFilterChange('all')}
                />
              )}
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              onClick={handleMarkAllAsRead}
              disabled={!notifications.some(n => !n.is_read)}
              size="small"
            >
              Mark All as Read
            </Button>
          </Box>

          {/* Filter Options */}
          {showFilters && (
            <Box mt={2}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by</InputLabel>
                <Select
                  value={filter}
                  label="Filter by"
                  onChange={(e) => handleFilterChange(e.target.value)}
                  size="small"
                >
                  <MenuItem value="all">All Notifications</MenuItem>
                  <MenuItem value="unread">Unread Only</MenuItem>
                  <MenuItem value="appointment">Appointments</MenuItem>
                  <MenuItem value="communication">Communications</MenuItem>
                  <MenuItem value="reminder">Reminders</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Notifications List */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              {filteredNotifications().length === 0 ? (
                <Box textAlign="center" py={4}>
                  <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No notifications found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {filter === 'all' ? "You're all caught up!" : `No ${filter} notifications`}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {paginatedNotifications.map((notification, index) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        button
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          backgroundColor: notification.is_read ? 'transparent' : '#f8f9fa',
                          '&:hover': {
                            backgroundColor: notification.is_read ? '#f5f5f5' : '#e3f2fd'
                          }
                        }}
                      >
                        <ListItemIcon>
                          {getNotificationIcon(notification.notification_type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography
                                variant="body1"
                                fontWeight={notification.is_read ? 'normal' : 'bold'}
                                sx={{ flex: 1, minWidth: 0 }}
                              >
                                {notification.content}
                              </Typography>
                              <Chip
                                label={notification.notification_type.replace('_', ' ')}
                                color={getNotificationColor(notification.notification_type)}
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {formatNotificationTime(notification.created_at)}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < paginatedNotifications.length - 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Results Summary */}
          {filteredNotifications().length > 0 && (
            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredNotifications().length)} of {filteredNotifications().length} notifications
              </Typography>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default NotificationsPage; 